import type { Entry, RateCacheEntry } from '@/types/domain'
import { type CryptoOptions, encryptString } from '@/core/crypto/encrypt'
import {
  SCHEMA_URI_ENCRYPTED,
  SCHEMA_URI_PLAIN,
  type ExportPayloadEncrypted,
  type ExportPayloadPlain,
} from './schema'

export interface ExportInput {
  entries: Entry[]
  anoBase: number | 'all'
  rateCache?: RateCacheEntry[]
  knownCategorias?: string[]
  knownTags?: string[]
  knownPagadores?: string[]
  /** When provided, the output is wrapped in an AES-GCM envelope. */
  password?: string
  /** Override for tests — production uses defaults. */
  now?: () => Date
  cryptoOptions?: CryptoOptions
}

/**
 * Builds the export JSON string. Plain by default; wraps in
 * ExportPayloadEncrypted when a password is supplied.
 */
export async function serializeExport(input: ExportInput): Promise<string> {
  const entries =
    input.anoBase === 'all' ? input.entries : input.entries.filter((e) => e.anoBase === input.anoBase)

  const plain: ExportPayloadPlain = {
    $schema: SCHEMA_URI_PLAIN,
    version: 1,
    exportedAt: (input.now?.() ?? new Date()).toISOString(),
    anoBase: input.anoBase,
    entries,
    ...(input.rateCache ? { rateCache: input.rateCache } : {}),
    ...(input.knownCategorias || input.knownTags || input.knownPagadores
      ? {
          settings: {
            knownCategorias: input.knownCategorias ?? [],
            knownTags: input.knownTags ?? [],
            knownPagadores: input.knownPagadores ?? [],
          },
        }
      : {}),
  }

  if (!input.password) {
    return JSON.stringify(plain, null, 2)
  }

  const envelope = await encryptString(JSON.stringify(plain), input.password, input.cryptoOptions)
  const wrapped: ExportPayloadEncrypted = {
    $schema: SCHEMA_URI_ENCRYPTED,
    version: 1,
    encrypted: true,
    algorithm: envelope.algorithm,
    kdf: envelope.kdf,
    salt: envelope.salt,
    iv: envelope.iv,
    ciphertext: envelope.ciphertext,
  }
  return JSON.stringify(wrapped, null, 2)
}

/**
 * Triggers a browser download of `content` as `filename`. Browser-only.
 */
export function downloadAsFile(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}

export function defaultFilename(anoBase: number | 'all', date: Date = new Date()): string {
  const tsIso = date.toISOString().replace(/[:.]/g, '-')
  const scope = anoBase === 'all' ? 'todos' : String(anoBase)
  return `folha-fiscal_${scope}_${tsIso}.json`
}

export function defaultFilenameText(
  anoBase: number | 'all',
  date: Date = new Date(),
): string {
  const tsIso = date.toISOString().replace(/[:.]/g, '-')
  const scope = anoBase === 'all' ? 'todos' : String(anoBase)
  return `folha-fiscal_${scope}_${tsIso}.txt`
}

// ---------------------------------------------------------------------------
// Plain-text export (carnê-leão style)
//
// Per-entry layout, blank line between entries:
//
//   {pagador}
//   {valor BRL pt-BR}
//   {moeda} {valor original pt-BR} ({data recebimento estilizada})
//   USD {valor USD pt-BR} ({data PTAX aplicável estilizada})
//
// Only calculated entries are emitted (the format only makes sense once a
// snapshot is available). The receipt-date line uses the original currency;
// the second numeric line is the USD intermediate computed by the converter.
// ---------------------------------------------------------------------------

const BRL_FMT = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const ORIG_FMT = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
})

const MONTH_ABBR_PT = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'] as const

function stylize(iso: string | undefined): string {
  if (!iso) return ''
  const parts = iso.split('-')
  if (parts.length !== 3) return iso
  const [year, month, day] = parts
  if (!year || !month || !day) return iso
  const monthIdx = Number.parseInt(month, 10) - 1
  const abbr = MONTH_ABBR_PT[monthIdx]
  if (!abbr) return iso
  return `${day}·${abbr.toUpperCase()}·${year.slice(2)}`
}

export interface TextExportInput {
  entries: Entry[]
  anoBase: number | 'all'
}

export function serializeAsText(input: TextExportInput): string {
  const filtered =
    input.anoBase === 'all'
      ? input.entries
      : input.entries.filter((e) => e.anoBase === input.anoBase)

  const blocks: string[] = []
  for (const entry of filtered) {
    if (entry.snapshot.status !== 'calculado') continue
    const block = formatEntryAsText(entry)
    if (block) blocks.push(block)
  }
  return blocks.join('\n\n')
}

function formatEntryAsText(entry: Entry): string {
  const valorBRL = entry.snapshot.valorBRL
  const valorUSD = entry.snapshot.valorUSD
  const dataPTAX = entry.snapshot.dataPTAXAplicavel
  if (valorBRL === undefined || valorUSD === undefined || !dataPTAX) return ''

  const lines = [
    entry.pagador ?? '',
    `BRL ${BRL_FMT.format(valorBRL)}`,
    `${entry.moedaOrigem} ${ORIG_FMT.format(entry.valorOriginal)} (${stylize(entry.dataRecebimento)})`,
    `USD ${ORIG_FMT.format(valorUSD)} (${stylize(dataPTAX)})`,
  ]
  return lines.join('\n')
}
